import React, { useState, useEffect } from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import api from "../../services/api";
import CompassCopilotWidget from "../../components/compass/CompassCopilotWidget";

const SESSION_STORAGE_KEY = "accbf_session_id";

function getOrCreateSessionId() {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = "accbf_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

// ─── Palette — same restrained, professional design language as the
// AmyloidConnect education rebuild (INK/SLATE neutrals), but a distinct
// warm amber accent instead of teal — same quality bar, visually its own
// vertical rather than a reskinned AmyloidConnect page. ─────────────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#B45309";        // warm amber
const ACCENT_SOFT = "#FFFBEB";
const PAPER = "#FFFFFF";
const CANVAS = "#F8FAFC";

const SOURCES = [
  { id: "clinicaltrials", label: "ClinicalTrials.gov", available: true },
  { id: "nci", label: "NCI Clinical Trials", available: true },
  { id: "211", label: "211 Resources", available: true }, // flips to false automatically if the backend 211 call isn't ready yet — see note below
];

export default function CompassPage() {
  const [sessionId] = useState(getOrCreateSessionId);
  const [condition, setCondition] = useState("");
  const [location, setLocation] = useState("");
  const [maxAge, setMaxAge] = useState("18");
  const [activeSources, setActiveSources] = useState(["clinicaltrials", "nci", "211"]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [previousSearch, setPreviousSearch] = useState(null); // { condition, location, createdAt } — shown as a banner, not auto-loaded
  const [interpretRequest, setInterpretRequest] = useState(null); // triggers the Copilot widget to explain a specific result card

  useEffect(() => {
    api.get("/v1/accbf/search/previous", { params: { sessionId } })
      .then((response) => {
        if (response.status === 200 && response.data) {
          setPreviousSearch(response.data);
        }
      })
      .catch(() => {}); // 204 No Content when nothing's saved yet — nothing to do
  }, [sessionId]);

  const toggleSource = (id) => {
    setActiveSources((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const runSearch = async (overrideCondition, overrideLocation, overrideMaxAge) => {
    const searchCondition = overrideCondition ?? condition;
    const searchLocation = overrideLocation ?? location;
    const searchMaxAge = overrideMaxAge ?? maxAge;
    if (!searchCondition.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const response = await api.get("/v1/accbf/search", {
        params: {
          condition: searchCondition.trim(),
          location: searchLocation.trim() || undefined,
          maxAge: searchMaxAge || undefined,
          sources: activeSources.join(","),
          sessionId,
        },
      });
      setResults(response.data);
      setPreviousSearch(null); // this search is now the current one, not a "previous" one to offer
    } catch (err) {
      setError("We ran into an issue reaching one or more sources. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => runSearch();

  const handleViewPrevious = () => {
    if (!previousSearch) return;
    setCondition(previousSearch.condition || "");
    setLocation(previousSearch.location || "");
    setResults(previousSearch.results);
    setPreviousSearch(null);
  };

  const handleClearPrevious = async () => {
    try {
      await api.delete("/v1/accbf/search", { params: { sessionId } });
    } catch (err) {
      // Non-critical — clear the banner locally either way
    }
    setPreviousSearch(null);
  };

  return (
    <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
      `}</style>

      {/* ── Header ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 6, md: 8 }, pb: { xs: 5, md: 6 }, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ maxWidth: 780 }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 2 }}>
            Compass — Built on the VeraInsight Platform
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.9rem", md: "2.6rem" }, fontWeight: 600, lineHeight: 1.18, color: INK, mb: 2.5 }}>
            One search. Every resource your family needs.
          </Typography>
          <Typography sx={{ fontSize: "0.98rem", color: SLATE, maxWidth: 620, lineHeight: 1.7 }}>
            This pilot brings together clinical trials from ClinicalTrials.gov and the National
            Cancer Institute, alongside local support services from 211 — searchable in one
            place, built on the same platform already serving amyloidosis patients today.
          </Typography>
        </Box>
      </Box>

      {/* ── Previous search banner ── */}
      {previousSearch && (
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 4, md: 5 } }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
            border: `1px solid ${BORDER}`, borderRadius: "10px", p: 2, bgcolor: CANVAS, flexWrap: "wrap",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <HistoryIcon sx={{ color: ACCENT, fontSize: 20 }} />
              <Typography sx={{ fontSize: "0.85rem", color: SLATE }}>
                You have a previous search saved
                {previousSearch.condition ? <> — <strong style={{ color: INK }}>{previousSearch.condition}</strong></> : null}
                {previousSearch.location ? <> near <strong style={{ color: INK }}>{previousSearch.location}</strong></> : null}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={handleViewPrevious}
                sx={{ textTransform: "none", fontWeight: 600, color: ACCENT }}
              >
                View Results
              </Button>
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={handleClearPrevious}
                sx={{ textTransform: "none", color: MUTED }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Search panel ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 5, md: 6 } }}>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "14px", p: { xs: 3, md: 4 }, bgcolor: PAPER }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            {SOURCES.map((s) => {
              const isActive = activeSources.includes(s.id);
              return (
                <Box
                  key={s.id}
                  onClick={() => toggleSource(s.id)}
                  sx={{
                    px: 2, py: 0.75, borderRadius: "8px", cursor: "pointer",
                    fontSize: "0.8rem", fontWeight: 600,
                    border: `1px solid ${isActive ? ACCENT : BORDER}`,
                    bgcolor: isActive ? ACCENT_SOFT : "transparent",
                    color: isActive ? ACCENT : MUTED,
                    transition: "all 0.15s ease",
                  }}
                >
                  {s.label}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr auto" }, gap: 2 }}>
            <TextField
              placeholder="Condition (e.g. leukemia, sarcoma)"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              size="small"
            />
            <TextField
              placeholder="Location or ZIP"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              size="small"
            />
            <TextField
              placeholder="Max age"
              value={maxAge}
              onChange={(e) => setMaxAge(e.target.value)}
              size="small"
              type="number"
            />
            <Button
              onClick={handleSearch}
              disabled={loading || !condition.trim()}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SearchIcon />}
              sx={{
                bgcolor: ACCENT, color: "#FFFFFF", fontWeight: 600, px: 3,
                textTransform: "none", borderRadius: "8px",
                "&:hover": { bgcolor: "#92400E" },
                "&.Mui-disabled": { bgcolor: BORDER },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Results ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, pb: { xs: 8, md: 10 } }}>
        {error && (
          <Typography sx={{ color: "#B91C1C", fontSize: "0.9rem", mb: 3 }}>{error}</Typography>
        )}

        {results && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={async () => {
                  try { await api.delete("/v1/accbf/search", { params: { sessionId } }); } catch (e) {}
                  setResults(null);
                }}
                sx={{ textTransform: "none", color: MUTED }}
              >
                Clear Results
              </Button>
            </Box>
            {results.clinicaltrials && (
              <ResultSection title="ClinicalTrials.gov" items={results.clinicaltrials} accent={ACCENT} onInterpret={(item) => setInterpretRequest({ ...item, key: Date.now() })} />
            )}
            {results.nci && (
              <ResultSection title="NCI Clinical Trials" items={results.nci} accent={ACCENT} onInterpret={(item) => setInterpretRequest({ ...item, key: Date.now() })} />
            )}
            {results["211"] && (
              <ResultSection title="211 Local Resources" items={results["211"]} accent={ACCENT} onInterpret={(item) => setInterpretRequest({ ...item, key: Date.now() })} />
            )}
          </Box>
        )}
      </Box>

      <CompassCopilotWidget onSearchRequest={(condition) => runSearch(condition, "", maxAge)} interpretRequest={interpretRequest} />
    </Box>
  );
}

function ResultSection({ title, items, accent, onInterpret }) {
  if (!items || items.length === 0) {
    return (
      <Box>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: INK, mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: "0.85rem", color: MUTED }}>No matching results found for this search.</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: INK, mb: 2 }}>
        {title} <span style={{ color: MUTED, fontWeight: 500 }}>({items.length})</span>
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((item, i) => (
          <Box key={i} sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", p: 2.5 }}>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: INK, mb: 0.5 }}>
              {item.title}
            </Typography>
            {item.org && (
              <Typography sx={{ fontSize: "0.8rem", color: accent, fontWeight: 600, mb: 1 }}>
                {item.org}
              </Typography>
            )}
            {item.description && (
              <Typography sx={{ fontSize: "0.85rem", color: SLATE, lineHeight: 1.6, mb: 1.5 }}>
                {item.description}
              </Typography>
            )}
            {onInterpret && (
              <Button
                size="small"
                onClick={() => onInterpret(item)}
                sx={{ textTransform: "none", fontWeight: 600, color: accent, fontSize: "0.78rem", px: 0 }}
              >
                🤖 Interpret with Copilot
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}